// ============================================================================
// Runtime configuration
// ----------------------------------------------------------------------------
// The same built bundle runs in our SaaS and in a customer's own AWS account,
// so the identity provider cannot be baked in at build time — a self-hosted
// deployment federates to whatever IdP that customer already runs, and we do
// not build a bundle per customer.
//
// The server therefore serves the configuration and the app fetches it before
// it renders anything. /api/config is the one unauthenticated endpoint, for
// the obvious reason: you cannot authenticate until you know where to.
// ============================================================================

export interface RuntimeConfig {
  /**
   * The server is running with a development identity and is not verifying
   * tokens. Only ever true locally — the server refuses to enable it outside
   * APP_ENV=local.
   */
  devAuth: boolean;
  /** OIDC issuer URL — a Cognito user pool, Entra, Okta, Google. */
  oidcIssuer: string;
  oidcClientId: string;
  oidcScopes: string;
  /**
   * Cognito's managed-login /logout URL. Cognito's sign-out is not standard
   * OIDC logout, so the browser builds the request itself. Empty for an
   * issuer that is not Cognito.
   */
  logoutEndpoint: string;
  /** "saas" or "self_hosted". */
  mode: "saas" | "self_hosted";
  /**
   * The theme of the org this hostname belongs to, so the sign-in screen is
   * in that org's look before anyone signs in (ADR 0010). Absent on the
   * shared hostname, which belongs to no org.
   */
  theme?: string;
}

let cached: RuntimeConfig | null = null;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  const res = await fetch("/api/config", { credentials: "omit" });
  if (!res.ok) {
    throw new Error(
      `The application could not load its configuration (${res.status}). ` +
        `The server may still be starting.`,
    );
  }
  cached = (await res.json()) as RuntimeConfig;
  return cached;
}
